import { AlertTriangle, RefreshCw, ChevronLeft, ChevronRight } from "lucide-react";
import SearchInput from "../../common/SearchInput";
import PageBtn from "./PageBtn";
import { useCommunicationErrors } from "./useCommunicationErrors";
import { channelIcon, formatDate, buildPageNumbers } from "./communicationError.utils";
import {
  cellBorder, trackingPill, channelPill, orgPill, errorText, navBtn,
} from "./communicationError.styles";

const COLUMNS = [
  { label: "S. No.", width: 50 },
  { label: "Tracking Number", width: 130 },
  { label: "Channel", width: 100 },
  { label: "Error Message", width: 330 },
  { label: "Organization", width: 180 },
  { label: "Created At", width: 150 },
];

export default function CommunicationErrorsTable() {
  const {
    isAdmin,
    errors, pagination, loading, fetchError,
    page, setPage, limit,
    organisations, selectedOrgId, setSelectedOrgId, orgLoading,
    searchQuery, setSearchQuery,
  } = useCommunicationErrors();

  const totalPages = pagination?.totalPages ?? 1;
  const canPrev = page > 1;
  const canNext = page < totalPages;
  const pageNumbers = buildPageNumbers(page, totalPages);

  return (
    <div style={{ fontFamily: "Inter, sans-serif", padding: "10px 0", background: "#f5f6fa", minHeight: "100vh" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "20px", flexWrap: "wrap", gap: "12px" }}>
        <div>
          {pagination && (
            <p style={{ margin: 0, fontSize: "13px", color: "#6b7280" }}>
              {pagination.total} error{pagination.total !== 1 ? "s" : ""} found
            </p>
          )}
        </div>

        <SearchInput placeholder="Search Errors" value={searchQuery} onChange={setSearchQuery} width={400} />

        {isAdmin && (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <select
              value={selectedOrgId}
              onChange={(e) => setSelectedOrgId(e.target.value)}
              disabled={orgLoading}
              style={{
                borderRadius: "8px", border: "1px solid #e5e7eb", background: "#fff",
                cursor: "pointer", outline: "none", minWidth: "180px", padding: "10px",
              }}
            >
              <option value="">Select Organisation.</option>
              {organisations.map((org) => (
                <option key={org._id} value={org._id}>{org.name}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Table card */}
      <div>
        <div style={{
          background: "#fff", borderRadius: "10px", boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
          overflow: "hidden", display: "flex", flexDirection: "column", minHeight: "560px",
        }}>
          {loading ? (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1, minHeight: "350px", color: "#9ca3af" }}>
              <RefreshCw size={24} style={{ animation: "spin 1s linear infinite", marginBottom: "12px" }} />
            </div>
          ) : fetchError ? (
            <div style={{ padding: "60px", textAlign: "center", color: "#ef4444", flex: 1 }}>
              <AlertTriangle size={28} style={{ marginBottom: "12px" }} />
              <p style={{ margin: 0 }}>{fetchError}</p>
            </div>
          ) : (
            <div className="communicationErrorTable" style={{ overflow: "hidden auto", width: "100%", flex: 1 }}>
              <table style={{ width: "100%", tableLayout: "fixed", borderCollapse: "collapse" }}>
                <colgroup>
                  {COLUMNS.map((c) => (
                    <col key={c.label} style={c.width ? { width: `${c.width}px` } : undefined} />
                  ))}
                </colgroup>
                <thead>
                  <tr style={{ background: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
                    {COLUMNS.map((c) => (
                      <th
                        key={c.label}
                        style={{
                          textAlign: "left",
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                          whiteSpace: "nowrap",
                          boxSizing: "border-box",
                          width: c.width ? `${c.width}px` : "auto",
                        }}
                      >
                        {c.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {errors.length === 0 ? (
                    <tr>
                      <td colSpan={6} style={{ padding: "48px", textAlign: "center", color: "#9ca3af", fontSize: "14px" }}>
                        No communication errors found.
                      </td>
                    </tr>
                  ) : (
                    errors.map((err, i) => (
                      <tr key={err._id}>
                        <td style={{  fontSize: "13px", color: "#9ca3af", fontWeight: 600, boxSizing: "border-box", ...cellBorder }}>
                          {(page - 1) * limit + i + 1}
                        </td>
                        <td style={{  boxSizing: "border-box", overflow: "hidden", ...cellBorder }}>
                          <span style={trackingPill}>{err?.trackingNumber || ""}</span>
                        </td>
                        <td style={{  boxSizing: "border-box", ...cellBorder }}>
                          <span style={channelPill}>{channelIcon(err.channel)}{err.channel}</span>
                        </td>
                        <td
                          style={{  boxSizing: "border-box", overflow: "hidden", ...cellBorder }}
                          title={err?.error?.message || err?.error?.errorMessage || ""}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0 }}>
                            <AlertTriangle size={14} color="#ef4444" style={{ flexShrink: 0 }} />
                            <span style={errorText}>
                              {err?.error?.message || err?.error?.errorMessage || ""}
                            </span>
                          </div>
                        </td>
                        <td style={{  boxSizing: "border-box", overflow: "hidden", ...cellBorder }}>
                          <span style={orgPill}>{err?.organisation?.organizationName}</span>
                        </td>
                        <td style={{  fontSize: "13px", color: "#6b7280", whiteSpace: "nowrap", boxSizing: "border-box", ...cellBorder }}>
                          {formatDate(err.createdAt)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination footer */}
          {pagination && !loading && !fetchError && (
            <div style={{
              padding: "12px 16px", borderTop: "1px solid #f3f4f6",
              display: "flex", justifyContent: "space-between", alignItems: "center",
              flexWrap: "wrap", gap: "10px", flexShrink: 0, background: "#fff",
            }}>
              <span style={{ fontSize: "13px", color: "#9ca3af" }}>
                Showing {errors.length === 0 ? 0 : (page - 1) * limit + 1}–
                {Math.min(page * limit, pagination.total)} of {pagination.total} records
              </span>

              <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <button onClick={() => setPage(page - 1)} disabled={!canPrev} style={navBtn(canPrev)}>
                  <ChevronLeft size={15} />
                </button>

                {pageNumbers[0] > 1 && (
                  <>
                    <PageBtn n={1} current={page} onClick={setPage} />
                    {pageNumbers[0] > 2 && <span style={{ padding: "0 4px", color: "#9ca3af" }}>…</span>}
                  </>
                )}

                {pageNumbers.map((n) => (
                  <PageBtn key={n} n={n} current={page} onClick={setPage} />
                ))}

                {pageNumbers[pageNumbers.length - 1] < totalPages && (
                  <>
                    {pageNumbers[pageNumbers.length - 1] < totalPages - 1 && (
                      <span style={{ padding: "0 4px", color: "#9ca3af" }}>…</span>
                    )}
                    <PageBtn n={totalPages} current={page} onClick={setPage} />
                  </>
                )}

                <button onClick={() => setPage(page + 1)} disabled={!canNext} style={navBtn(canNext)}>
                  <ChevronRight size={15} />
                </button>
              </div>
            </div>
          )}
        </div>

        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );
}